import { LanguageCode } from '../languages';
import { User } from './users';
import { CouponRedemptionMini } from 'recurly';

type GroupsDataType = {
  groupID: number;
  name: string;
  active: number;
  features: string;
  promptRemove: boolean;
  url: string;
  visibility: number;
};

export enum UserSetting {
  SET_PUSH_PREFERENCE = 'setPushPreference',
  PUSH_FREQ = 'pushFreq',
  PUSH_SNOOZE = 'pushSnooze',
  CURRENT_STATUS = 'currentStatus',
  LAST_PUSH = 'lastPush',
  LAST_MARKETING_PUSH = 'lastMarketingPush',
  TIMEZONE = 'timezone',
  WANTS_RESTART = 'wantsRestart',
  VERSION = 'version',
  RECENT_MODULES = 'recentModules',
  PRIME = 'prime',
  LAST_PUSH_NODE = 'lastPushNode',
  TODAYS_SCHEDULE = 'todaysSchedule',
  TODAYS_SCHEDULE_LOCAL = 'todaysSchedule_local',
  GROUP_SCHEDULE = 'groupSchedule',
  TODAYS_EXAM_SCHEDULE = 'todaysExamSchedule',
  TODAYS_FOCUS_QUES_FC_SCHEDULE = 'todaysFocusQuesFCSchedule',
  TODAYS_CLINICAL_SCHEDULE = 'todaysClinicalSchedule',
  TODAYS_DUE_STEP1_SCHEDULE = 'todaysDueStep1Schedule',
  TODAYS_DUE_STEP1_DAILY_SCHEDULE = 'todaysDueStep1DailySchedule',
  TODAYS_DUE_STEP1V2_SCHEDULE = 'todaysDueStep1v2Schedule',
  HIDE_PICMONIC = 'hidePicmonic',
  SENT_WEB_ACCT_CHECK_EMAIL = 'sentWebAcctCheckEmail',
  LEGACY_FEATURES = 'legacyFeatures',
  DOC_VIEWER = 'docViewer',
  REPETITION_OVERRIDE = 'repetitionOverride',
  ANKI_BETA_DEPRECATED = 'ankiBeta_deprecated',
  DAY_STARTS_AT = 'dayStartsAt',
  ITEM_RESPONSE_COUNT = 'itemResponseCount',
  RECENT_FC_QUEUE_CUTOFF = 'recentFCQueueCutoff',
  RECENT_QUES_QUEUE_CUTOFF = 'recentQuesQueueCutoff',
  EMAIL_DAILY_SCHEDULE = 'emailDailySchedule',
  EMAIL_LEARN_UPDATES = 'emailLearnUpdates',
  EMAIL_CUSTOM_VID_RECS = 'emailCustomVidRecs',
  ANALYTICS_SESSION = 'analyticsSession',
  QUIZ_SETTINGS = 'quizSettings',
  QUIZ_BUILDER_OPT_IN = 'quizBuilderOptIn',
  SHOWN_MESSAGES = 'shownMessages',
  SHOW_CONFIRMATION = 'showConfirmation',
  ACHIEVEMENTS_BETA = 'achievementsBeta',
  DAILY_PRACTICE_QUIZ_BETA = 'dailyPracticeQuizBeta',
  DAILY_PRACTICE_QUIZ_BETA_LEADERBOARD = 'dailyPracticeQuizBetaLeaderboard',
  DAILY_PRACTICE_LEADERBOARD_VISIBLE = 'dailyPracticeLeaderboardVisible',
  DAILY_PRACTICE_LEADERBOARD_ANON = 'dailyPracticeLeaderboardAnon',
  DAILY_PRACTICE_NUMBER_OF_QUESTIONS = 'dailyPracticeNumberOfQuestions',
  DAILY_PRACTICE_EMAIL_SUBSCRIBE = 'dailyPracticeEmailSubscribe',
  DAILY_PRACTICE_QUIZ_CAMPAIGN = 'dailyPracticeQuizCampaign',
  TEMP_VIDEO_BLOCK = 'tempVideoBlock',
  ACTIVE_STREAMING_DEVICE = 'activeStreamingDevice',
  DAILY_VIEW_RESET = 'dailyViewReset',
  REMOVE_DAILY_VIEW_LIMIT = 'removeDailyViewLimit',
  FACULTY_STATUS = 'facultyStatus',
  SHARE_PLAYLIST = 'sharePlaylist',
  BILLING_NOTIFICATIONS = 'billingNotifications',
  TOTAL_PURCHASE_DATA = 'totalPurchaseData',
  ROLES = 'roles',
  USER_DISCOUNT = 'userDiscount',
  TEMP_PASSWORD = 'tempPassword',
  VIDEOS_WATCHED = 'videosWatched',
  SET_MARKETING_PUSH_PREFERENCE = 'setMarketingPushPreference',
  OVERRIDE_EMAIL_MUT = 'overrideEmailMut',
  LANGUAGE = 'language',
  DASHBOARD_PERSONALIZATION = 'dashboardPersonalization',
}

export enum PrimeStatusString {
  'Expired',
  'None',
  'Subscription',
  'B2B',
  'Canceled',
  'Paid Prime',
  'Free Trial',
  'Prime',
}

export enum PrimeStatus {
  EXPIRED = -1,
  FREE = 0, // Not-activated free trial
  PRIME = 1,
  TRIAL = 2,
}

export type AdminProps = Record<string, boolean>;

// Data pull directly from database
export type UserRowProps = {
  userID: number;
  preferredName: string;
  lastName: string;
  avatar: string;
  class: number;
  degree: number;
  joinDate: string | null;
  timezone: string;
  startOfDay: number;
  isEditor?: boolean;
  subProducts?: SubProduct[];
  googleId: string;
};

export type UserPrimeProps = {
  statusString: PrimeStatusString | string;
  status: PrimeStatus | number;
  product: string;
  primeExpires?: string;
  isAutoRenew: boolean;
  /** @deprecated Use isAutoRenew */
  autoRenew?: boolean;
  expires?: string;
  hasPaid: boolean;
  isB2B?: boolean | number;
  /** @deprecated Use isB2B */
  b2b?: boolean | number;
  /** True only if the user's currently active product is B2B (unlike isB2B/b2b, which is true for any B2B membership).
   * Kept as `boolean | null` (not widened like isB2B) — adding `| number` breaks the next-webapp prod build via an unrelated Next.js tracer bug; confirmed via bisection. */
  isActiveProductB2B?: boolean | null;
};

export type UserObjectPrimeProps = {
  statusString?: PrimeStatusString | string | null;
  status?: PrimeStatus | number | null;
  product?: string | null;
  primeExpires?: string | null;
  isAutoRenew?: boolean | null;
  /** @deprecated Use isAutoRenew */
  autoRenew?: boolean | null;
  expires?: string | null;
  hasPaid?: boolean | null;
  isB2B?: boolean | number | null;
  /** @deprecated Use isB2B */
  b2b?: boolean | number | null;
  /** True only if the user's currently active product is B2B (unlike isB2B/b2b, which is true for any B2B membership).
   * Kept as `boolean | null` (not widened like isB2B) — adding `| number` breaks the next-webapp prod build via an unrelated Next.js tracer bug; confirmed via bisection. */
  isActiveProductB2B?: boolean | null;
};

export enum UserProductComponentType {
  BOOK_REFS = 'bookRefs',
  EXTERNAL_LINKS = 'externalLinks',
  FLASHCARDS = 'flashcards',
  GUIDELINES = 'guidelines',
  HIGH_YIELD_NOTES = 'hyn',
  LANGUAGE_SELECTION = 'languageSelection',
  LOCALIZATIONS = 'localizations',
  LP_FLASHCARDS = 'lpFlashcards',
  LP_QUESTIONS = 'lpQuestions',
  NAVS = 'navs',
  PARTNER_LINKS = 'partnerLinks',
  QBANK = 'qbank',
  QUIZ_BUILDER = 'quizbuilder',
  FLASHCARD_BUILDER = 'flashcardbuilder',
  STUDY_SCHEDULE = 'studyschedule',
  VIDEOS = 'videos',
  OSMOSIS_AI = 'osmosisAi',
}

// Data added to user object during session load
export type UserProps = User & {
  language_name: string | null;
  productCode: string | null;
  language: LanguageCode | null;
  productComponents?: UserProductComponentType[];
  program: string | null;
  prime: UserPrimeProps;
  primeExtra: {
    count: number[];
    reason: number[];
    sum: number[];
  };
  productID: number | null;
  productLocalization: string | null;
  hasMultiProducts: boolean;
  hasLockedContentFlag: boolean;
  code: string;
  groups: GroupsDataType[];
  email: string;
  admin?: AdminProps;
  hasPaid?: boolean;
  profile?: UserProfileDataProps;
  geoLocation?: UserGeoLocation;
  subProducts: SubProduct[];
  clinicalBetaLaunch?: ClinicalBetaLaunchProps;
  address?: UserAddressProps;
  isFaculty: boolean;
  referralIsAvailable?: boolean;
};

export type OtherFieldProps = 'Patient' | 'Family' | 'Friend';
export type StatusProps = 'Faculty' | 'Graduated' | 'Other';
export type TypeProps = 'Professional' | 'Student' | 'Other';

// TODO: These are Programs, not Products
export enum UserProduct {
  MD = 'MD',
  DO = 'DO',
  PA = 'PA',
  RN = 'RN',
  NP = 'NP',
  PH = 'PH',
  OH = 'OH',
  LPN = 'LPN',
  DN = 'DN',
  'Pre-onboarding' = 'Pre-onboarding',
}

export type UserSearchFilter = {
  subProducts: string[];
};

export type UserSearchFilterKeys = keyof UserSearchFilter;

export type UserProfileDataProps = {
  /**
   * Raw onboarding program choice stored in user_profiles JSON.
   * Valid for DB-level reads (UserProfileRowProps).
   * @deprecated Not available on the cached UserObject.profile — stripped in OSMS-18107-6.
   * Read the resolved program alias from `user.product` (top-level) instead.
   */
  product: UserProduct;
  searchFilter?: UserSearchFilter;
  /**
   * @deprecated Not available on the cached UserObject.profile — stripped in OSMS-18107-5.
   * Read from `user.productID` (top-level) instead.
   */
  productID?: number;
  degree?: string;
  type?: TypeProps;
  school?: string;
  schoolID?: number;
  graduationYear?: number;
  otherField?: OtherFieldProps;
  status?: StatusProps;
  classYear?: string;
  specialty?: string;
  subSpecialty?: string;
  stage?: string;
  examPrep?: string;
  linkedIn?: string;
  dob?: string;
  cohortSchool?: string;
  cohortSchoolID?: number;
};

export type UserGeoLocation = {
  country_code?: string;
  country_name?: string;
};

export type UserProfileRowProps = {
  userID: number;
  schoolID: number;
  profile: UserProfileDataProps;
  addedAt: string | null;
  deletedAt: string | null;
  modified: string;
};

export type ClinicalBetaLaunchProps = {
  isBetaProduct: boolean;
  allowedProductId: number;
};

export type UserObjectType = {
  address: UserAddressProps;
  admin?: AdminProps;
  avatar?: string;
  language: string;
  language_name: string;
  productCode: string;
  canOptInQuizBuilder: boolean;
  cmeIsEnabled: boolean;
  clinicalBetaLaunch?: ClinicalBetaLaunchProps;
  code: string;
  course: {
    type: string;
    schoolType: string;
  };
  day: {
    date: string;
    begin: string;
    end: string;
    startsAt: number;
    utc: string;
  };
  email: string;
  first: string;
  geoLocation?: UserGeoLocation;
  groups: GroupsDataType[];
  hasMultiProducts: boolean;
  hasLockedContentFlag: boolean;
  hasPaid: boolean;
  isFaculty: boolean;
  isOnMonthlyBillingTest: boolean;
  joined: string;
  last: string;
  latestTerms: boolean;
  payment_notifications?: object;
  primeExtra: {
    count: number[];
    reason: number[];
    sum: number[];
  };
  prime?: UserObjectPrimeProps;
  primeStatus: PrimeStatusString;
  primeExpires: string;
  primeStatusID: PrimeStatus;
  primeAutoRenew: boolean;
  primeB2B: boolean;
  referralIsAvailable?: boolean;
  product: string;
  productID: number;
  productComponents: UserProductComponentType[];
  productLocalization: string;
  profile: UserProfileDataProps;
  schoolData: SchoolDataType;
  settings: {
    quizSettings: object;
    shownMessages: object;
  };
  videos: number[];
  videosWatched: number;
  subProducts?: SubProduct[];
};

export type SubProduct = {
  groupName: string;
  state: SubProductState;
};

export enum SubProductState {
  active = 'active',
  locked = 'locked',
  expiring = 'expiring',
}

export type UserObjectProps = {
  user: UserObjectType;
  success: number;
};

export type UserNameProps = {
  preferredName: string;
  lastName: string;
};

export type UserEmailProps = {
  userID: number;
  email: string;
  primary?: number;
};

export type UserAddressProps = {
  shippingAddress: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
};

export type SchoolDataType = {
  countryID: number;
  formalName: string;
  nickname: string;
  schoolID: number;
  url: string;
  verifiedSchool: boolean;
};

export type UserAccountSettingsProps = {
  hasPassword: boolean;
  preferredName: string;
  lastName: string;
  emails: UserEmailSettingsProps;
  inventoryCodes: UserInventoryCodesSettingsProps;
  inventoryPacks: UserInventoryPacksSettingsProps;
  groups: UserGroupsSettingsProps;
  success: number;
};

export type UserEmailSettingsProps = {
  primary: string[];
  secondary: string[];
  pending: string[];
  unverified: string[];
};

export type UserInventoryCodesSettingsProps = {
  inventoryID: number[];
  code: string[];
};

export type UserInventoryPacksSettingsProps = {
  archived: number[];
  active: number[];
};

export type UserGroupsSettingsProps = {
  groupID: number[];
  name: string;
  active: number[];
  schoolID: number[];
};

export type QueueBadgeProps = {
  flagged: {
    fc: number[];
    ques: number[];
  };
  repetition: number;
  success: number;
};

export type ActiveProductSourceType = {
  type: string;
  name: string;
  term: string;
  program?: string;
  userProgram?: string;
  data: {
    subscriptionID: number;
    planID: number;
    productID: number;
    userID: number;
    state: string;
    currentTermStartedAt: string;
    currentTermEndsAt: string;
    createdAt: string;
    modifiedAt: string;
    deletedAt: string | null;
    type: number;
    autoRenew: number;
    data: string;
  };
};

export type ActiveProductType = {
  productID: number;
  program: string;
  alias: string;
  sources: ActiveProductSourceType[];
  displayName?: string;
};

export type UserActiveProductsProps = {
  products?: ActiveProductType[];
};

export type RecurlyInvoiceProps = {
  invoices?: RecurlyInvoiceType[];
  success: number;
};

export type RecurlyInvoiceType = {
  number: number;
  userID: number;
  userBillingID: number;
  id: string;
  previousInvoiceId?: number;
  state: string;
  type: string;
  origin: string;
  paid: number;
  refundableAmount: number;
  subtotal: number;
  total: number;
  currency: string;
  tax: number;
  discount: number;
  balance: number;
  createdAt: string;
  updatedAt: string;
  dueAt: string;
  closedAt: string;
  dealURL?: string;
  userCardID: number;
  firstName: string;
  lastName: string;
  street1: string;
  street2?: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  paymentMethod: string;
  lastFour: string;
  brand: string;
};

// formerly RecurlySubscription
export type RecurlySubscription = {
  id: string;
  userID: number;
  subscriptionID: number;
  hash: string;
  state: string;
  currentTermStartedAt: string;
  currentTermEndsAt: string;
  type: number;
  data: string;
  autoRenew: number;
  productID: number;
};

export type RecurlyPlan = {
  recurlyPlanID?: number;
  planID?: number;
  hash?: string;
  id?: string;
  code: string;
  state?: string;
  intervalLength: number;
  totalBillingCycles?: number;
  autoRenew: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  productID?: number;
  name: string;
  product: string;
  upgradesTo?: number;
  created?: string;
  updated?: string;
  data?: string;
  activeCoupon?: string;
  mostRecentChargePreTax?: number;
  nextChargePreTax?: number;
  currency: {
    base: number;
    currency: string;
    setupFee: number;
    unitAmount: number;
    withCouponRedemption?: boolean;
    originalUnitAmount?: number;
  };
  lastCouponRedemption?: CouponRedemptionMini;
};

export type RecurlyBillingInfo = {
  city: string;
  country: string;
  firstName: string;
  lastName: string;
  postalCode: string;
  region: string;
  street1: string;
  street2?: string;
  paymentMethod: string;
};

export type RecurlySubscriptionProps = {
  notFound?: boolean;
  restricted?: boolean;
  success: number;
  subscription?: RecurlySubscription;
  plan?: RecurlyPlan;
  billingInfo?: RecurlyBillingInfo;
  cardInfo?: {
    lastFour: string;
    brand: string;
  };
  pastDue?: boolean;
};

export interface UserProfileStats {
  segment: string;
  userCount: number;
  classYearKnown: number;
  classYearKnownRatio: number;
  fieldKnown: number;
  fieldKnownRatio: number;
  schoolKnown: number;
  schoolKnownRatio: number;
}
